import type { RouteStop, TripMode, TspResult, TspShape } from '@/types';
import { findPath } from './pathfinding';

/**
 * Optimisation de l'ordre des étapes (Traveling Salesman avec contraintes).
 *
 * Stratégie :
 * - n ≤ 9 free slots → énumération exhaustive (max 362 880 permutations)
 * - n > 9 free slots → nearest-neighbor + 2-opt (heuristique correcte)
 *
 * Toutes les variantes respectent `lockedAt` (étapes figées à leur position).
 * `shape` = 'open' (ligne A→B) ou 'loop' (boucle, retour au premier).
 */

/**
 * Construit la matrice de coûts entre toutes les paires d'étapes.
 * `Infinity` si pas de chemin trouvé dans le mode donné.
 */
export function buildCostMatrix(stops: RouteStop[], mode: TripMode): number[][] {
  const n = stops.length;
  const mat: number[][] = [];
  for (let i = 0; i < n; i++) {
    mat[i] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) {
        mat[i][j] = 0;
        continue;
      }
      const path = findPath(stops[i].id, stops[j].id, mode);
      mat[i][j] = path ? path.total : Infinity;
    }
  }
  return mat;
}

/**
 * Coût d'une permutation complète (open ou loop).
 * Infinity si une arête est manquante.
 */
function pathCost(order: number[], matrix: number[][], shape: TspShape): number {
  const n = order.length;
  let c = 0;
  for (let i = 0; i < n - 1; i++) {
    const a = order[i],
      b = order[i + 1];
    if (matrix[a][b] === Infinity) return Infinity;
    c += matrix[a][b];
  }
  if (shape === 'loop' && n > 1) {
    if (matrix[order[n - 1]][order[0]] === Infinity) return Infinity;
    c += matrix[order[n - 1]][order[0]];
  }
  return c;
}

/**
 * Résolveur exhaustif par permutation (pour petits cas).
 * On ne permute QUE les indices libres, dans les SLOTS libres.
 */
function solveExhaustive(
  matrix: number[][],
  skeleton: number[],
  freeIndices: number[],
  freeSlots: number[],
  shape: TspShape,
): { order: number[] | null; cost: number } {
  let best: number[] | null = null;
  let bestCost = Infinity;
  const n = matrix.length;

  function permute(arr: number[], k: number) {
    if (k === arr.length) {
      const full = skeleton.slice();
      for (let s = 0; s < freeSlots.length; s++) full[freeSlots[s]] = arr[s];
      const cost = pathCost(full, matrix, shape);
      if (cost < bestCost) {
        bestCost = cost;
        best = full.slice();
      }
      return;
    }
    for (let i = k; i < arr.length; i++) {
      [arr[k], arr[i]] = [arr[i], arr[k]];
      permute(arr, k + 1);
      [arr[k], arr[i]] = [arr[i], arr[k]];
    }
  }

  permute(freeIndices.slice(), 0);
  // Si pas de solution trouvée, on garde n pour la cohérence du caller
  void n;
  return { order: best, cost: bestCost };
}

/**
 * Résolveur heuristique : nearest-neighbor pour seed, puis 2-opt local.
 * Les positions verrouillées sont préservées.
 */
function solveHeuristic(
  matrix: number[][],
  skeleton: number[],
  freeIndices: number[],
  freeSlots: number[],
  shape: TspShape,
): { order: number[]; cost: number } {
  const n = matrix.length;
  const current = skeleton.slice();

  // Seed : nearest-neighbor en parcourant les slots dans l'ordre
  const available = new Set(freeIndices);
  let prevIdx = -1;
  for (let pos = 0; pos < n; pos++) {
    if (current[pos] !== -1) {
      prevIdx = current[pos];
      continue;
    }
    let bestNext = -1;
    let bestC = Infinity;
    available.forEach((c) => {
      const cost = prevIdx === -1 ? 0 : matrix[prevIdx][c];
      if (cost < bestC) {
        bestC = cost;
        bestNext = c;
      }
    });
    if (bestNext === -1) {
      bestNext = available.values().next().value!;
    }
    current[pos] = bestNext;
    available.delete(bestNext);
    prevIdx = bestNext;
  }

  // 2-opt sur les positions libres uniquement
  let improved = true;
  let iters = 0;
  let working = current.slice();
  while (improved && iters < 50) {
    improved = false;
    iters++;
    for (let a = 0; a < freeSlots.length - 1; a++) {
      for (let b = a + 1; b < freeSlots.length; b++) {
        const posA = freeSlots[a];
        const posB = freeSlots[b];
        const trial = working.slice();
        [trial[posA], trial[posB]] = [trial[posB], trial[posA]];
        if (pathCost(trial, matrix, shape) < pathCost(working, matrix, shape)) {
          working = trial;
          improved = true;
        }
      }
    }
  }

  return { order: working, cost: pathCost(working, matrix, shape) };
}

/**
 * Solveur principal.
 * `lockedAt[pos]` = index originel à figer à la position `pos`, -1 si libre.
 */
export function solveTSP(
  matrix: number[][],
  lockedAt: number[],
  shape: TspShape,
): TspResult {
  const n = matrix.length;

  const skeleton = new Array(n).fill(-1);
  lockedAt.forEach((idx, pos) => {
    if (idx !== null && idx >= 0 && idx < n) skeleton[pos] = idx;
  });

  const lockedSet = new Set<number>();
  skeleton.forEach((v) => {
    if (v >= 0) lockedSet.add(v);
  });

  const freeIndices: number[] = [];
  for (let i = 0; i < n; i++) if (!lockedSet.has(i)) freeIndices.push(i);

  const freeSlots: number[] = [];
  for (let i = 0; i < n; i++) if (skeleton[i] === -1) freeSlots.push(i);

  // n ≤ 9 free → énumération exhaustive
  if (freeIndices.length <= 9) {
    const { order, cost } = solveExhaustive(
      matrix,
      skeleton,
      freeIndices,
      freeSlots,
      shape,
    );
    return { order: order ?? [], cost };
  }

  // sinon heuristique
  return solveHeuristic(matrix, skeleton, freeIndices, freeSlots, shape);
}
